import React from "react";
import { Box, Typography } from "@mui/material";
import {
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useSharedColors } from "../../theme/SharedComponentsTheme";

export interface CustomTimeInputProps {
  /** 時刻文字列 ("HH:mm") */
  value: string;
  onChange: (v: string) => void;
  label?: string;
  placeholder?: string;
  width?: string;
  size?: "sm" | "md";
  disabled?: boolean;
  name?: string;
}

/**
 * CustomTimeInput
 *
 * `@mui/x-date-pickers` の `TimePicker` をベースに、
 * `CustomTextInput` / `CustomSingleSelect` と同じ
 * 角丸・ボーダー・フォントで統一する。
 *
 * value は "HH:mm" 文字列で受け渡し、内部では Dayjs に変換。
 */
const CustomTimeInput: React.FC<CustomTimeInputProps> = ({
  value,
  onChange,
  label,
  placeholder,
  width = "100%",
  size = "md",
  disabled = false,
  name,
}) => {
  const colors = useSharedColors();
  void size;

  // "HH:mm" → Dayjs
  const parsedValue: Dayjs | null = React.useMemo(() => {
    if (!value) return null;
    const today = dayjs().format("YYYY-MM-DD");
    const d = dayjs(`${today}T${value}`);
    return d.isValid() ? d : null;
  }, [value]);

  return (
    <Box sx={{ width, display: "flex", flexDirection: "column" }}>
      {label && (
        <Typography
          sx={{
            fontSize: "12px",
            color: colors.text.primary,
            mb: "1px",
            paddingBottom: "4px",
          }}
        >
          {label}
        </Typography>
      )}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          value={parsedValue}
          ampm={false}
          disabled={disabled}
          onChange={(d) => {
            if (!d || !d.isValid()) {
              onChange("");
              return;
            }
            onChange(d.format("HH:mm"));
          }}
          slotProps={{
            textField: {
              name,
              placeholder,
              size: "small",
              sx: {
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  height: { xs: "32px", sm: "34px" },
                  borderRadius: "8px",
                  backgroundColor: colors.background.default,
                  "& fieldset": { borderColor: colors.border.light },
                  "&:hover fieldset": { borderColor: colors.border.light },
                  "&.Mui-focused fieldset": {
                    borderColor: `${colors.border.light} !important`,
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: "8px 12px",
                  fontSize: "12px",
                  color: colors.text.primary,
                  "&::placeholder": {
                    color: colors.text.placeholder,
                    opacity: 0.8,
                    fontSize: "12px",
                  },
                },
                "& .MuiInputAdornment-root .MuiIconButton-root": {
                  padding: "4px",
                  marginRight: "-4px",
                  color: colors.text.secondary,
                  "& svg": {
                    width: 18,
                    height: 18,
                  },
                },
              },
            },
            // ピッカー本体 (時計ダイアログ): primary 色をプロジェクトに合わせる
            popper: {
              sx: {
                "& .MuiPickersToolbar-root": {
                  display: "none",
                },
                "& .MuiClock-pin, & .MuiClockPointer-root, & .MuiClockPointer-thumb":
                  {
                    backgroundColor: colors.primary.main,
                    borderColor: colors.primary.main,
                  },
                "& .MuiClockPointer-thumb": {
                  border: `16px solid ${colors.primary.main}`,
                },
                "& .MuiClockNumber-root.Mui-selected": {
                  backgroundColor: colors.primary.main,
                  color: colors.text.inverse,
                },
                "& .MuiButton-root": {
                  color: colors.primary.main,
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default CustomTimeInput;
